import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { FiSend, FiX } from 'react-icons/fi';
import { useVirtual } from 'react-virtual';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';

import { Nullable } from '@common/types';
import { ChatMessage, ReceivedChatMessage } from '@common/types/chat-socket';
import { useAuth, useBroadcastQuery, useChatMessages } from '@hooks';
import { selectUser } from '@hooks/useAuth/selectors';
import { CHAT_CLIENT_ID } from '@constants';
import { api } from '@services';

import { BaseInteractionProps } from '../../types';
import Message from './Message';
import PinnedMessage from './PinnedMessage';
import * as S from './styles';
import * as I from '../styles';

type FormInputs = {
  message: string;
};

type ReplyData = {
  author_name: string;
  content: string;
};

const Chat = ({ active: isActive }: BaseInteractionProps) => {
  const user = useAuth(selectUser);
  const { t } = useTranslation();

  const { data: isChatEnabled } = useBroadcastQuery({
    select: ({ settings }) => !!settings.data.chat,
    notifyOnChangeProps: ['data'],
  });

  const { data: broadcastData } = useBroadcastQuery({
    notifyOnChangeProps: ['data'],
  });

  const pinnedMessage = broadcastData?.pinnedMessage?.data ?? null;
  const roomId = broadcastData?.id;

  const { messages, groupId, groupKey, socket } = useChatMessages();
  const [replyTo, setReplyTo] = useState<ReplyData | null>(null);

  const formMethods = useForm<FormInputs>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      Yup.object().shape({
        message: Yup.string().required('valid.message.req'),
      }),
    ),
  });
  const { handleSubmit, reset } = formMethods;

  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  // eslint-disable-next-line no-nested-ternary
  const inputPlaceholder = user.blocked_chat
    ? t('pages.broadcast.chat.blocked')
    : replyTo
    ? `Respondendo ${replyTo.author_name}...`
    : t('pages.broadcast.chat.send');

  const parentRef = useRef<Nullable<HTMLElement>>(null);

  const rowVirtualizer = useVirtual({
    size: messages.length,
    parentRef,
    estimateSize: useCallback(() => 65, []),
    keyExtractor: useCallback((index) => messages[index].id, [messages]),
  });
  const { virtualItems, scrollToOffset, totalSize } = rowVirtualizer;

  const handleSendNewMessage = ({ message }: FormInputs) => {
    reset();

    socket?.emit('chat.message', {
      client_id: CHAT_CLIENT_ID,
      group_id: groupId,
      group_key: groupKey,
      author_id: `${user.id}`,
      author_name: user.name,
      author_avatar: user.avatar,
      moderator: !!user.moderator,
      fixed: false,
      content: message,
      reply_to_author: replyTo?.author_name,
      reply_to_content: replyTo?.content,
    } as ChatMessage);

    setReplyTo(null);

    scrollToOffset(totalSize, {
      align: 'end',
    });

    setHasUserScrolled(false);
  };

  const handleReply = useCallback((data: ReplyData) => {
    setReplyTo(data);
    // Focus no input após setar reply
    setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>(
        'input[name="message"]',
      );
      input?.focus();
    }, 50);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyTo(null);
  }, []);

  const handlePin = useCallback(
    async (message: ReceivedChatMessage) => {
      try {
        await api.post(`/rooms/${roomId}/pinned-message`, {
          message_id: message.id,
          author_name: message.author_name,
          content: message.content,
        });
        toast.success('Mensagem fixada');
      } catch {
        toast.error('Erro ao fixar mensagem');
      }
    },
    [roomId],
  );

  const handleUnpin = useCallback(async () => {
    try {
      await api.delete(`/rooms/${roomId}/pinned-message`);
      toast.success('Mensagem desafixada');
    } catch {
      toast.error('Erro ao desafixar mensagem');
    }
  }, [roomId]);

  const handleContainerRef = useCallback((node: HTMLElement) => {
    if (node) {
      parentRef.current = node;
    }
  }, []);

  const handleChatListScroll = useCallback(
    (hasScrolled: boolean) => () => setHasUserScrolled(hasScrolled),
    [],
  );

  useEffect(() => {
    if (hasUserScrolled) {
      return;
    }

    scrollToOffset(totalSize, {
      align: 'end',
    });
  }, [messages, hasUserScrolled, scrollToOffset, totalSize]);

  return (
    <S.Container
      autoComplete="off"
      onSubmit={handleSubmit(handleSendNewMessage)}
      $disabled={!isActive}
      $hide={!isChatEnabled}
    >
      <FormProvider {...formMethods}>
        {pinnedMessage && (
          <PinnedMessage
            message={pinnedMessage}
            isModerator={!!user.moderator}
            onUnpin={handleUnpin}
          />
        )}

        <I.InteractionWrapper>
          <S.ChatContentWrapper
            containerRef={handleContainerRef}
            onScroll={handleChatListScroll(true)}
            onYReachEnd={handleChatListScroll(false)}
          >
            <I.VirtualizedList $height={totalSize}>
              {virtualItems.map((virtualRow) => (
                <I.VirtualizedItemWrapper
                  key={messages[virtualRow.index].id}
                  ref={virtualRow.measureRef}
                  $translateY={virtualRow.start}
                >
                  <Message
                    socket={socket}
                    groupId={groupId}
                    groupKey={groupKey}
                    pinnedMessageId={pinnedMessage?.message_id}
                    onPin={handlePin}
                    onReply={handleReply}
                    {...messages[virtualRow.index]}
                  />
                </I.VirtualizedItemWrapper>
              ))}
            </I.VirtualizedList>
          </S.ChatContentWrapper>
        </I.InteractionWrapper>

        {replyTo && (
          <S.ReplyBanner>
            <S.ReplyBannerContent>
              <span>Respondendo a {replyTo.author_name}</span>
              <p>{replyTo.content}</p>
            </S.ReplyBannerContent>
            <S.ReplyBannerClose type="button" onClick={handleCancelReply}>
              <FiX size={14} />
            </S.ReplyBannerClose>
          </S.ReplyBanner>
        )}

        <S.ChatInput
          required
          type="text"
          name="message"
          buttonIcon={FiSend}
          autoFocus={isActive}
          placeholder={inputPlaceholder}
          disabled={!!user.blocked_chat || !isChatEnabled}
        />
      </FormProvider>
    </S.Container>
  );
};

export default memo(Chat);
